/**
 * Formato editorial das apresentações (vem do ManualMaker, pptx.js): capa escura com uma
 * coluna na cor da marca, sumário (com 5 ou mais passos), um diapositivo por passo e fecho.
 * Aqui ficam só as contas (paleta a partir da cor da marca, encaixe de texto e de imagens);
 * o PowerPoint é gerado na web com pptxgenjs.
 *
 * O Google Slides ignora o ajuste automático de texto do PPTX, por isso o tamanho da letra
 * é calculado aqui, com uma largura média por carácter prudente.
 */
import { isHexColor, readableAccent, textOn } from '../brand-kit';

/** Diapositivo 16:9, em polegadas. */
export const SLIDE = { w: 10, h: 5.625, mx: 0.5 } as const;

/** Sumário: linhas por coluna e duas colunas por diapositivo. */
export const TOC_PER_COL = 14;
export const TOC_PER_SLIDE = TOC_PER_COL * 2;
/** O sumário só aparece com pelo menos este número de passos. */
export const TOC_MIN_STEPS = 5;

export const LINE_HEIGHT = 1.32;

export const pad2 = (n: number) => (n < 10 ? '0' : '') + n;

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

const toHex = (c: number[]) =>
  c
    .map((v) => Math.round(v).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();

/** Mistura duas cores (t = 0 → a, t = 1 → b). Devolve RRGGBB sem «#» (formato do pptxgenjs). */
export function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return toHex(x.map((v, i) => v + (y[i]! - v) * t));
}

export interface EditorialPalette {
  /** Fundo da capa e do fecho (por omissão, a cor principal muito escura). */
  dark: string;
  /** Texto sobre a capa. */
  onDark: string;
  /** Coluna da capa e do fecho: a cor principal. */
  column: string;
  /** Texto sobre a coluna. */
  onColumn: string;
  /** Régua e data sobre a capa (por omissão, a cor principal clara). */
  highlight: string;
  /** Números e régua nos diapositivos claros (contraste ≥ 4,5:1 com o fundo). */
  number: string;
  /** Fundo dos diapositivos de conteúdo. */
  paper: string;
  /** Texto nos diapositivos claros. */
  ink: string;
}

const bare = (hex: string) => hex.replace('#', '').toUpperCase();
const LIGHT = 'F7F6F2';

/**
 * Cores do formato editorial a partir do kit de marca: a cor principal e, opcionais, a da
 * capa e a de destaque. O texto sobre a capa e a coluna fica claro ou escuro, o que se ler
 * melhor. Devolve RRGGBB sem «#».
 */
export function editorialPalette(
  accent: string,
  cover?: string | null,
  highlight?: string | null,
): EditorialPalette {
  const a = isHexColor(accent) ? accent : '#C2410C';
  const ink = mix(a, '#000000', 0.82);
  const dark = cover && isHexColor(cover) ? bare(cover) : mix(a, '#000000', 0.78);
  const onDark = textOn('#' + dark) === '#ffffff' ? LIGHT : ink;
  return {
    dark,
    onDark,
    column: bare(a),
    onColumn: textOn(a) === '#ffffff' ? LIGHT : ink,
    highlight: highlight && isHexColor(highlight) ? bare(highlight) : mix(a, '#FFFFFF', 0.55),
    number: bare(readableAccent(a)),
    paper: LIGHT,
    ink,
  };
}

/** Número de linhas de um texto com cpl caracteres por linha (quebra por palavras). */
export function lineCount(text: string, cpl: number): number {
  let lines = 0;
  for (const par of String(text).split('\n')) {
    let cur = 0;
    lines++;
    for (const w of par.split(/\s+/).filter(Boolean)) {
      const l = w.length;
      if (cur === 0) cur = l;
      else if (cur + 1 + l <= cpl) cur += 1 + l;
      else {
        lines++;
        cur = l;
      }
      while (cur > cpl) {
        lines++;
        cur -= cpl;
      }
    }
  }
  return lines;
}

const cpl = (wIn: number, pt: number) => Math.max(1, Math.floor((wIn * 72) / (pt * 0.52)));

export interface Fitted {
  pt: number;
  text: string;
  lines: number;
  cut: boolean;
}

/**
 * Maior tamanho de letra entre maxPt e minPt com que o texto cabe na caixa (polegadas);
 * abaixo disso, corta por palavras com reticências.
 */
export function fitText(
  text: string,
  wIn: number,
  hIn: number,
  maxPt: number,
  minPt: number,
): Fitted {
  const t = String(text || '').trim();
  for (let pt = maxPt; pt >= minPt; pt--) {
    const n = lineCount(t, cpl(wIn, pt));
    if ((n * pt * LINE_HEIGHT) / 72 <= hIn) return { pt, text: t, lines: n, cut: false };
  }
  const c = cpl(wIn, minPt);
  const maxLines = Math.max(1, Math.floor((hIn * 72) / (minPt * LINE_HEIGHT)));
  const words = t.split(/\s+/);
  while (words.length > 1 && lineCount(words.join(' ') + '…', c) > maxLines) words.pop();
  let out = words.join(' ');
  if (lineCount(out + '…', c) > maxLines) out = out.slice(0, c * maxLines - 1);
  return { pt: minPt, text: out.replace(/[\s.,;:—–-]+$/, '') + '…', lines: maxLines, cut: true };
}

/** Altura (polegadas) de um texto já encaixado. */
export const fittedHeight = (f: Fitted) => (f.lines * f.pt * LINE_HEIGHT) / 72;

/** Encaixe «contain»: o maior tamanho possível dentro da área, centrado. */
export function fitRect(iw: number, ih: number, x: number, y: number, w: number, h: number) {
  if (!iw || !ih) [iw, ih] = [16, 9];
  const s = Math.min(w / iw, h / ih);
  return { x: x + (w - iw * s) / 2, y: y + (h - ih * s) / 2, w: iw * s, h: ih * s };
}

/**
 * Layout de um passo: «wide» (título em cima, captura em toda a largura) para capturas
 * largas com texto curto; «columns» (texto à esquerda, captura à direita) no resto.
 */
export function stepLayout(aspect: number | null, title: string, desc: string): 'wide' | 'columns' {
  if (aspect === null || aspect < 1.3) return 'columns';
  if (fitText(title, 7.95, 0.42, 20, 15).cut) return 'columns';
  if (desc && fitText(desc, 7.95, 0.42, 11, 10).cut) return 'columns';
  return 'wide';
}
